import { ApiError } from '../core/api/http'
import { nowStamp, today } from '../core/format/dates'
import { nextCreditNoteNumber } from '../core/rules/kassa'
import { returnLines, returnProblem, returnTotalCents } from '../core/rules/saleReturn'
import { db, type SaleReturnRow } from './db'
import { demoId, reply } from './reply'
import { demoAdminIdentity } from './adminIdentity'
import { bookSaleStock } from './stockBackend'
import { issueRefundCredit } from './voucherBackend'
import type { AdminBackend } from '../core/types/backend'
import type { Sale, SaleReturn, SaleReturnRequest } from '../core/types/kassa'

/**
 * DEMO — retour op een kassabon (core/rules/saleReturn.ts), aan de kant van de server. Op elke dag,
 * ook na de afsluiting van de bon; de retour telt op vandaag. Pin of contant door de kassa van
 * vandaag, tegoed op het grootboek van de klant. Producten met `restock` terug op de salonvoorraad;
 * anders blijven ze afgeboekt (kapot, geopend).
 */

function fail(message: string, status: number, code?: string, field?: string): never {
  throw new ApiError(message, status, field ? { [field]: message } : undefined, code)
}

export const stripReturn = ({ requestId: _r, ...x }: SaleReturnRow): SaleReturn => x

/** De retouren op één bon, oudste eerst. */
export const returnsOfSale = (saleId: string): SaleReturn[] =>
  db
    .saleReturns()
    .filter((x) => x.saleId === saleId)
    .sort((a, b) => a.at.localeCompare(b.at))
    .map(stripReturn)

/** Retouren die op `day` met pin of contant zijn terugbetaald: die gaan door de kassa van die dag. */
export const returnsOf = (day: string): SaleReturn[] =>
  db
    .saleReturns()
    .filter((x) => x.day === day && x.method !== 'tegoed' && x.refundCents > 0)
    .sort((a, b) => b.at.localeCompare(a.at))
    .map(stripReturn)

/** Alle creditnotanummers (gestopte kuren én retouren): één reeks zonder gaten. */
export const creditNoteNumbers = () => [...db.kuurStops().map((k) => k.number), ...db.saleReturns().map((x) => x.number)]

function returnSale(saleId: string, req: SaleReturnRequest): SaleReturn {
  const known = db.saleReturns().find((x) => x.requestId === req.requestId)
  if (known) return stripReturn(known)
  if (demoAdminIdentity.access().grants['kassa.refund'] !== 'all') fail('Een retour doet alleen Sarah.', 403, 'forbidden')

  const row = db.sales().find((s) => s.id === saleId)
  if (!row) return fail('Deze bon bestaat niet.', 404)
  const sale: Sale = { ...row, returns: returnsOfSale(row.id) }
  if (sale.status !== 'betaald') fail('Deze bon is gecorrigeerd; er valt niets meer terug te nemen.', 409, 'voided')
  const reason = req.reason.trim()
  const problem = returnProblem(sale, { lines: req.lines, method: req.method, reason })
  if (problem) fail(problem.message, 422, problem.field === 'lines' ? 'line_invalid' : problem.field, problem.field)

  const day = today()
  const lines = returnLines(sale, req.lines)
  const refundCents = returnTotalCents(lines)
  // Geld via de kassa: alleen als de kassa van vandaag nog openstaat (de la is anders al geteld).
  if (refundCents > 0 && req.method !== 'tegoed' && db.cashDay(day)) {
    fail('De kassa van vandaag is al afgesloten. Betaal terug als tegoed, of morgen via de kassa.', 409, 'day_closed', 'method')
  }

  const number = nextCreditNoteNumber(creditNoteNumbers(), day)
  const ret: SaleReturnRow = {
    id: demoId('rt').toLowerCase(),
    requestId: req.requestId,
    number,
    at: nowStamp(),
    day,
    saleId: sale.id,
    saleNumber: sale.number,
    saleDay: sale.day,
    ...(sale.customerId ? { customerId: sale.customerId } : {}),
    customerName: sale.customerName,
    lines,
    refundCents,
    method: req.method,
    restock: req.restock,
    reason,
    createdBy: demoAdminIdentity.current().name,
    accountId: demoAdminIdentity.current().accountId,
  }
  db.addSaleReturn(ret)
  if (req.method === 'tegoed' && sale.customerId) issueRefundCredit(sale.customerId, refundCents, ret.id, `Retour bon ${sale.number} (${number})`)
  if (req.restock) {
    const back = lines.flatMap((l) => {
      const line = sale.lines[l.lineIndex]
      return line?.kind === 'product' ? [{ productId: line.productId, qty: l.qty }] : []
    })
    bookSaleStock(back, 'salon', 1, number, `Retour bon ${sale.number}: ${reason}`)
  }
  return stripReturn(ret)
}

export const demoSaleReturnBackend: Pick<AdminBackend, 'returnSale'> = {
  async returnSale(saleId, req) {
    // Eerst rekenen en valideren, dan pas "netwerk": een fout komt als 422/409 terug, net als bij de server.
    try {
      return await reply(returnSale(saleId, req), 500)
    } catch (err) {
      await reply(null, 300)
      throw err
    }
  },
}
